/**
 * Step / Getting Started page chrome (titles, quotes, breadcrumbs, actions heading).
 */

import { BreadcrumbItem } from '../components/Header';
import { resolveMessage, t, tStepTemplate } from '../i18n/translate';

const STEP_PATHS: Record<number, string> = {
  0: '/saa/12-steps/getting-started',
  1: '/saa/12-steps/step-1',
  2: '/saa/12-steps/step-2',
  3: '/saa/12-steps/step-3',
  4: '/saa/12-steps/step-4',
  5: '/saa/12-steps/step-5',
  6: '/saa/12-steps/step-6',
  7: '/saa/12-steps/step-7',
  8: '/saa/12-steps/step-8',
  9: '/saa/12-steps/step-9',
  10: '/saa/12-steps/step-10',
  11: '/saa/12-steps/step-11',
  12: '/saa/12-steps/step-12',
};

function stepName(n: number): string {
  return resolveMessage(`stepName.${n}`);
}

export function getStepPageTitle(stepNumber: number): string {
  if (stepNumber === 0) return t('getting_started.page.title');
  return tStepTemplate('step.page.title', stepNumber);
}

export function getStepPageQuote(stepNumber: number): string {
  if (stepNumber === 0) return t('getting_started.page.subtitle');
  return t(`step.${stepNumber}.quote`);
}

export function getActionsToCompleteTitle(stepNumber: number): string {
  if (stepNumber === 0) return t('getting_started.page.actions_title');
  return tStepTemplate('step.page.actions_title', stepNumber);
}

export function getActionsIntro(): string {
  return t('step.page.actions_intro');
}

export function getStepLoadingMessage(): string {
  return t('step.page.loading');
}

export function getRefreshPageLabel(): string {
  return t('ui.REFRESH_PAGE');
}

export function getCongratsHeading(): string {
  return t('step.page.congrats_heading');
}

export function getGettingStartedCongratsHeading(): string {
  return t('getting_started.page.congrats_heading');
}

/** Body for completing step N (0–12). */
export function getCongratsBody(stepNumber: number): string {
  if (stepNumber === 0) return t('getting_started.page.congrats_body');
  if (stepNumber === 12) return t('step.12.congrats_body');
  return t('step.page.congrats_body', {
    stepName: stepName(stepNumber),
    nextStepName: stepName(stepNumber + 1),
  });
}

export function getPrevStepNavLabel(stepNumber: number): string {
  if (stepNumber === 1) return t('step.page.prev_getting_started');
  if (stepNumber === 0) return '';
  return t('step.page.prev_link', { stepName: stepName(stepNumber - 1) });
}

export function getNextStepNavLabel(stepNumber: number): string {
  if (stepNumber === 0) return t('getting_started.page.next_link');
  if (stepNumber >= 12) return '';
  return t('step.page.next_link', { stepName: stepName(stepNumber + 1) });
}

export function getGuidanceModalTitle(stepNumber: number): string {
  return tStepTemplate('step.page.guidance_title', stepNumber);
}

export function getVideoGuideTitle(stepNumber: number): string {
  return tStepTemplate('step.page.video_title', stepNumber);
}

export function getStepPath(stepNumber: number): string {
  return STEP_PATHS[stepNumber] || '/saa/12-steps';
}

export function getStepBreadcrumbs(stepNumber: number): BreadcrumbItem[] {
  const lastLabel = getStepPageTitle(stepNumber);
  const lastPath = getStepPath(stepNumber);
  return [
    { label: t('nav.home'), path: '/', active: false },
    { label: t('nav.saa'), path: '/saa', active: false },
    {
      label: t('nav.12_steps'),
      path: stepNumber === 0 ? '/saa?tab=steps' : '/saa/12-steps',
      active: false,
    },
    { label: lastLabel, path: lastPath, active: true },
  ];
}
